import { useAppContext } from "@soft-db/ui";

export class ColorEvaluator {
	#div: HTMLDivElement | null = null;
	#ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null = null;

	/**
	 * Color can be anything that is possible for the css color attributes.
	 * The output is a rgba string of the evaluated color, using the browser.
	 */
	evaluate(color: string, attrs: Record<string, string | null | undefined> = {}): string {
		if (this.#div == null) {
			this.#div = document.createElement("div");
			this.#div.id = "color-resolver";
			this.#div.style.display = "none";
			document.body.appendChild(this.#div);
		}

		if (this.#ctx == null) {
			if (typeof OffscreenCanvas !== "undefined") {
				this.#ctx = new OffscreenCanvas(1, 1).getContext("2d");
			} else {
				const canvas = document.createElement("canvas");
				canvas.width = 1;
				canvas.height = 1;
				this.#ctx = canvas.getContext("2d");
			}
		}

		if (this.#ctx == null) {
			return "";
		}

		for (const [key, val] of Object.entries(attrs)) {
			if (val == null) {
				continue
			}
			this.#div.setAttribute(key, val);
		}

		this.#div.style.color = color;

		const computedColor = window.getComputedStyle(this.#div).color;

		this.#ctx.clearRect(0, 0, 1, 1);

		this.#ctx.fillStyle = computedColor;
		this.#ctx.fillRect(0, 0, 1, 1);

		const [r, g, b, a] = this.#ctx.getImageData(0, 0, 1, 1).data;

		for (const key of Object.keys(attrs)) {
			this.#div.removeAttribute(key);
		}

		return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`;
	}
}

export function useColorEvaluator() {
	const evaluator = new ColorEvaluator();

	const { dark } = useAppContext();

	return function (color: string, attrs: Record<string, string | null | undefined> = {}): string {
		if (attrs["data-theme"] == null) {
			attrs["data-theme"] = dark.value ? "dark" : "light";
		}

		return evaluator.evaluate(color, attrs);
	};
}
